import { Link } from 'react-router-dom';
import type { Product } from '@harbor/shared';
import { money, stockLabel } from '../lib/format.js';
import { Badge, Button } from './ui.js';
import { useCart } from '../state/cart.js';
import { useState } from 'react';

/**
 * Product card.
 *
 * The "Add to basket" button is disabled when the item is out of stock rather
 * than hidden, and the reason is always visible. A button that disappears leaves
 * the customer wondering whether the page loaded correctly; a disabled button
 * with "Sold out" next to it answers the question.
 */
export function ProductCard({ product, priority = false }: { product: Product; priority?: boolean }) {
  const { addItem, mutating } = useCart();
  const [adding, setAdding] = useState(false);

  const primary = product.images[0];
  const stock = stockLabel(product.stock);
  const soldOut = product.stock <= 0;
  const discounted =
    product.compareAtMinor !== null && product.compareAtMinor > product.priceMinor;
  const discountPercent = discounted
    ? Math.round((1 - product.priceMinor / (product.compareAtMinor ?? product.priceMinor)) * 100)
    : 0;

  async function handleAdd() {
    setAdding(true);
    try {
      await addItem(product.id, 1);
    } finally {
      setAdding(false);
    }
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-xl border border-line bg-surface transition-colors duration-200 hover:border-line-strong">
      <Link
        to={`/product/${product.slug}`}
        className="relative block aspect-4/3 overflow-hidden bg-sunken"
      >
        {primary ? (
          <img
            src={primary.url}
            alt={primary.alt}
            /* The first row of the grid is above the fold; everything below it
               is lazy. Loading all 24 eagerly costs bandwidth for images nobody
               has scrolled to yet. */
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : 'auto'}
            decoding="async"
            className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex size-full items-center justify-center">
            <span className="text-xs text-subtle">No image</span>
          </div>
        )}

        <div className="absolute left-3 top-3 flex gap-1.5">
          {discounted && <Badge tone="accent">−{discountPercent}%</Badge>}
          {soldOut && <Badge tone="danger">Sold out</Badge>}
          {!soldOut && product.stock <= 5 && <Badge tone="warning">Low stock</Badge>}
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-4">
        {product.category && (
          <p className="text-xs font-medium uppercase tracking-wide text-subtle">
            {product.category.name}
          </p>
        )}

        <h3 className="mt-1 text-sm font-semibold leading-snug">
          <Link to={`/product/${product.slug}`} className="hover:text-accent">
            {product.name}
          </Link>
        </h3>

        <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-muted">{product.summary}</p>

        <div className="mt-auto pt-4">
          <div className="flex items-baseline gap-2">
            <span className="tabular text-base font-semibold">
              {money(product.priceMinor, product.currency)}
            </span>
            {discounted && product.compareAtMinor !== null && (
              <span className="tabular text-xs text-subtle line-through">
                {money(product.compareAtMinor, product.currency)}
              </span>
            )}
          </div>

          <div className="mt-3 flex items-center justify-between gap-2">
            <span
              className={
                stock.tone === 'danger'
                  ? 'text-xs text-danger'
                  : stock.tone === 'warning'
                    ? 'text-xs text-warning'
                    : 'text-xs text-muted'
              }
            >
              {stock.text}
            </span>

            <Button
              size="sm"
              variant={soldOut ? 'secondary' : 'primary'}
              disabled={soldOut || mutating}
              loading={adding}
              onClick={() => void handleAdd()}
            >
              {soldOut ? 'Sold out' : 'Add'}
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
}

/** Matching skeleton, so the grid does not jump when data arrives. */
export function ProductCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-line bg-surface">
      <div className="skeleton aspect-4/3" />
      <div className="space-y-2.5 p-4">
        <div className="skeleton h-3 w-16 rounded" />
        <div className="skeleton h-4 w-3/4 rounded" />
        <div className="skeleton h-3 w-full rounded" />
        <div className="flex items-center justify-between pt-2">
          <div className="skeleton h-5 w-16 rounded" />
          <div className="skeleton h-8 w-16 rounded-lg" />
        </div>
      </div>
    </div>
  );
}
